import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Typography,
  Divider,
  useMediaQuery,
  useTheme,
  ClickAwayListener,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import PersonIcon from "@mui/icons-material/Person";
import {
  isDoctorSessionActive,
  isTenantSessionActive,
  isSuperAdminSessionActive,
} from "../../utils/authSession";
import BusinessIcon from "@mui/icons-material/Business";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LanguageSelector from "../LanguageSelector";
import { AnimatedBrandLogo } from "./landing/AnimatedBrandLogo";
import { BRAND, MARKETING_CONTAINER_SX } from "./landing/brandTokens";
import { MARKETING_NAV_COLLAPSE_QUERY } from "../../utils/responsiveScale";

const SCROLL_THRESHOLD = 32;

const glassBarStyles = {
  backgroundColor: "rgba(255, 255, 255, 0.92) !important",
  backdropFilter: "blur(24px) saturate(180%)",
  WebkitBackdropFilter: "blur(24px) saturate(180%)",
  boxShadow: "0 4px 32px rgba(10, 22, 40, 0.10), 0 1px 0 rgba(29,184,160,0.08)",
  borderBottom: `1px solid ${BRAND.borderLight}`,
};

const authPanelSx = {
  mt: 1.25,
  p: 1,
  minWidth: 248,
  borderRadius: "18px",
  border: `1px solid ${BRAND.borderLight}`,
  background: "rgba(255, 255, 255, 0.98)",
  backdropFilter: "blur(24px) saturate(160%)",
  boxShadow: `0 16px 40px rgba(10, 22, 40, 0.12), 0 0 24px ${BRAND.tealGlow}`,
};

function NavLink({ children, onClick }) {
  return (
    <Button
      variant="text"
      onClick={onClick}
      sx={{
        color: BRAND.navy,
        fontSize: { xs: "0.82rem", md: "0.84rem", lg: "0.86rem" },
        fontWeight: 600,
        px: { md: 1.25, lg: 1.5 },
        py: 0.75,
        minWidth: 0,
        textTransform: "none",
        whiteSpace: "nowrap",
        position: "relative",
        transition: "color 0.25s ease",
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 2,
          left: "50%",
          transform: "translateX(-50%)",
          width: "0%",
          height: "2px",
          borderRadius: "2px",
          background: `linear-gradient(90deg, ${BRAND.teal}, #a78bfa)`,
          transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: `0 0 8px ${BRAND.teal}88`,
        },
        "&:hover": {
          color: BRAND.teal,
          background: "transparent",
        },
        "&:hover::after": {
          width: "80%",
        },
      }}
    >
      {children}
    </Button>
  );
}

function AuthMenuItem({ icon: Icon, label, onClick, highlight = false }) {
  return (
    <Button
      fullWidth
      onClick={onClick}
      startIcon={<Icon sx={{ fontSize: 20, color: highlight ? BRAND.teal : BRAND.textSecondary }} />}
      sx={{
        justifyContent: "flex-start",
        gap: 1,
        px: 1.25,
        py: 1.15,
        borderRadius: "12px",
        textTransform: "none",
        color: BRAND.navy,
        fontWeight: highlight ? 700 : 600,
        fontSize: "0.9rem",
        bgcolor: highlight ? BRAND.tealLight : "transparent",
        border: highlight ? `1px solid ${BRAND.teal}44` : "1px solid transparent",
        transition: "all 0.22s ease",
        "&:hover": {
          bgcolor: highlight ? "rgba(29, 184, 160, 0.18)" : BRAND.sectionBgMuted,
          borderColor: `${BRAND.teal}55`,
        },
      }}
    >
      {label}
    </Button>
  );
}

function AccountMenuButton({ items, onNavigate, hoverOpen = true, label }) {
  const { t } = useTranslation("topbar");
  const [anchorEl, setAnchorEl] = useState(null);
  const [justClosed, setJustClosed] = useState(false);
  const closeTimer = useRef(null);
  const open = Boolean(anchorEl);

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const handleOpen = (event) => {
    if (justClosed) {
      setJustClosed(false);
      return;
    }
    clearCloseTimer();
    setAnchorEl(event.currentTarget);
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setAnchorEl(null), 160);
  };

  const handleClose = () => {
    clearCloseTimer();
    setAnchorEl(null);
    setJustClosed(true);
    setTimeout(() => {
      setJustClosed(false);
    }, 150);
  };

  const run = (fn) => {
    fn();
    handleClose();
  };

  useEffect(() => () => clearCloseTimer(), []);

  return (
    <>
      <Box
        onMouseEnter={hoverOpen ? handleOpen : undefined}
        onMouseLeave={hoverOpen ? scheduleClose : undefined}
        sx={{ display: "inline-flex", ml: 0.75 }}
      >
        <Button
          onClick={handleOpen}
          variant="contained"
          endIcon={
            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize: 22,
                transition: "transform 0.25s ease",
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          }
          sx={{
            height: 32,
            px: 1.5,
            background: `linear-gradient(135deg, ${BRAND.tealDark} 0%, ${BRAND.teal} 100%)`,
            color: "#fff",
            fontWeight: 700,
            fontSize: { xs: "0.80rem", md: "0.85rem" },
            textTransform: "none",
            borderRadius: "8px",
            border: `1px solid ${BRAND.teal}66`,
            boxShadow: open
              ? `0 8px 28px rgba(29, 184, 160, 0.45), 0 0 0 2px ${BRAND.tealGlow}`
              : "0 6px 20px rgba(15, 123, 107, 0.35)",
            transition: "all 0.25s ease",
            "&:hover": {
              background: `linear-gradient(135deg, ${BRAND.teal} 0%, #25c9ad 100%)`,
              boxShadow: `0 10px 32px rgba(29, 184, 160, 0.5)`,
            },
          }}
        >
          {label || t("accountMenu")}
        </Button>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableScrollLock
        hideBackdrop
        sx={{ pointerEvents: "none", zIndex: 1400 }}
        slotProps={{
          paper: {
            onMouseEnter: () => clearCloseTimer(),
            onMouseLeave: () => {
              if (hoverOpen) scheduleClose();
            },
            sx: { pointerEvents: "auto", ...authPanelSx },
          },
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Box sx={{ width: "100%", height: "100%" }}>
            <Typography
              sx={{
                px: 1.25,
                pt: 0.5,
                pb: 1,
                fontSize: "0.68rem",
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: BRAND.textSoft,
              }}
            >
              {t("accountMenuHint")}
            </Typography>

            <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
              {items.map((item) => (
                <Box component="li" key={item.key} sx={{ mb: 0.5 }}>
                  <AuthMenuItem
                    icon={item.icon}
                    label={item.label}
                    highlight={item.highlight}
                    onClick={() => run(() => onNavigate(item.path))}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </ClickAwayListener>
      </Popover>
    </>
  );
}

const ENABLE_PRICING = process.env.REACT_APP_ENABLE_PRICING === "true";

export default function MarketingNavbar() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation("topbar");
  const isMobile = useMediaQuery(MARKETING_NAV_COLLAPSE_QUERY);
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isCompactNav = useMediaQuery("(max-width: 1400px)");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerJustClosed, setDrawerJustClosed] = useState(false);

  const handleDrawerOpen = () => {
    if (drawerJustClosed) {
      setDrawerJustClosed(false);
      return;
    }
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setDrawerJustClosed(true);
    setTimeout(() => {
      setDrawerJustClosed(false);
    }, 150);
  };
  const [mobileAuthAnchor, setMobileAuthAnchor] = useState(null);
  const [mobileAuthJustClosed, setMobileAuthJustClosed] = useState(false);

  const handleMobileAuthOpen = (e) => {
    if (mobileAuthJustClosed) {
      setMobileAuthJustClosed(false);
      return;
    }
    setMobileAuthAnchor(e.currentTarget);
  };

  const handleMobileAuthClose = () => {
    setMobileAuthAnchor(null);
    setMobileAuthJustClosed(true);
    setTimeout(() => {
      setMobileAuthJustClosed(false);
    }, 150);
  };
  const [showGlass, setShowGlass] = useState(false);

  useEffect(() => {
    const handleScroll = (e) => {
      const target = e.target;
      if (!target) return;
      const scrollTop = target === document || target === window
        ? (window.scrollY || document.documentElement.scrollTop)
        : (target.scrollTop || 0);
      setShowGlass(scrollTop > SCROLL_THRESHOLD);
    };

    window.addEventListener("scroll", handleScroll, { capture: true, passive: true });

    // Initial check (in case page was already scrolled on mount)
    const elements = document.querySelectorAll("div");
    for (const el of elements) {
      const style = window.getComputedStyle(el);
      if ((style.overflowY === "auto" || style.overflowY === "scroll" || style.overflow === "auto" || style.overflow === "scroll") && el.scrollHeight > el.clientHeight) {
        if (el.scrollTop > SCROLL_THRESHOLD) {
          setShowGlass(true);
        }
        break;
      }
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, { capture: true });
    };
  }, []);

  const logoSize = isMdUp ? (isCompactNav ? 80 : 88) : 72;

  const leftItems = useMemo(
    () => {
      const items = [
        { text: t("home"), onClick: () => navigate("/") },
        { text: t("about"), onClick: () => navigate("/about") },
      ];
      if (ENABLE_PRICING) {
        items.push({ text: t("packages", { defaultValue: "Fiyatlandırma" }), onClick: () => navigate("/packages") });
      }
      items.push({ text: t("contact"), onClick: () => navigate("/contact") });
      return items;
    },
    [t, navigate]
  );

  const isLoggedIn =
    isDoctorSessionActive() ||
    isTenantSessionActive() ||
    isSuperAdminSessionActive();

  const dashboardPath = (() => {
    if (isSuperAdminSessionActive()) return "/super-admin/tenants";
    if (isTenantSessionActive()) return "/tenant-patients";
    return "/patients";
  })();

  const loginMenuItems = useMemo(() => [
    {
      key: "individual",
      label: t("individualLogin"),
      icon: PersonIcon,
      path: "/individual-login",
    },
    {
      key: "corporate",
      label: t("corporateLogin"),
      icon: BusinessIcon,
      path: "/corporate-login",
    }
  ], [t]);

  const registerMenuItems = useMemo(() => [
    {
      key: "register-individual",
      label: t("individualRegister", { defaultValue: "Bireysel Kayıt" }),
      icon: PersonAddAlt1OutlinedIcon,
      path: "/register-individual",
      highlight: true,
    },
    {
      key: "register",
      label: t("corporateRegister", { defaultValue: "Kurumsal Kayıt" }),
      icon: PersonAddAlt1OutlinedIcon,
      path: "/tenantRegister",
      highlight: true,
    },
  ], [t]);

  const drawerItems = useMemo(
    () => {
      const items = [
        { text: t("home"), icon: <HomeIcon />, onClick: () => navigate("/") },
        { text: t("about"), icon: <InfoIcon />, onClick: () => navigate("/about") },
      ];
      if (ENABLE_PRICING) {
        items.push({ text: t("packages", { defaultValue: "Fiyatlandırma" }), icon: <LocalOfferOutlinedIcon />, onClick: () => navigate("/packages") });
      }
      items.push({ text: t("contact"), icon: <ContactsOutlinedIcon />, onClick: () => navigate("/contact") });
      items.push({ text: t("pricing"), icon: <EventAvailableOutlinedIcon />, onClick: () => navigate("/meeting") });
      return items;
    },
    [t, navigate]
  );

  const goHome = () => {
    navigate("/");
    
    // Find the scrollable container and scroll it to the top
    const elements = document.querySelectorAll("div");
    let scrolled = false;
    for (const el of elements) {
      const style = window.getComputedStyle(el);
      if ((style.overflowY === "auto" || style.overflowY === "scroll" || style.overflow === "auto" || style.overflow === "scroll") && el.scrollHeight > el.clientHeight) {
        el.scrollTo({ top: 0, behavior: "smooth" });
        scrolled = true;
        break;
      }
    }
    if (!scrolled) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        sx={{
          width: "100%",
          left: 0,
          right: 0,
          top: 0,
          zIndex: 1400,
          backgroundImage: "none",
          overflow: "visible",
          transition: "box-shadow 0.3s ease, backdrop-filter 0.3s ease, background-color 0.3s ease",
          /* Gradient accent line at the bottom */
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "2px",
            background: `linear-gradient(90deg, transparent 0%, ${BRAND.teal}99 20%, #a78bfa99 50%, ${BRAND.teal}99 80%, transparent 100%)`,
            opacity: showGlass ? 1 : 0.45,
            transition: "opacity 0.3s ease",
            pointerEvents: "none",
          },
          ...(showGlass
            ? glassBarStyles
            : {
                backgroundColor: "transparent !important",
                boxShadow: "none",
                borderBottom: "none",
                backdropFilter: "none",
                WebkitBackdropFilter: "none",
              }),
        }}
      >
        <Box sx={{ position: "relative", width: "100%" }}>
          <Container maxWidth={false} disableGutters sx={MARKETING_CONTAINER_SX}>
            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: { xs: 66, md: 80 },
                pt: { xs: 2, md: 2.75 },
                pb: 0,
                overflow: "visible",
              }}
            >
              {/* Sol: menü + logo */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 0.5, md: 1 },
                  flexShrink: 0,
                  zIndex: 2,
                }}
              >
                {isMobile && (
                  <IconButton
                    onClick={handleDrawerOpen}
                    sx={{ color: BRAND.navy, ml: 1, flexShrink: 0 }}
                    aria-label="menü"
                  >
                    <MenuIcon sx={{ fontSize: 28 }} />
                  </IconButton>
                )}

                <Box
                  component={motion.button}
                  type="button"
                  onClick={goHome}
                  transition={{ type: "spring", stiffness: 320, damping: 28 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  sx={{
                    flexShrink: 0,
                    mt: 0,
                    p: 0,
                    minWidth: 0,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 0.75, md: 1.25 },
                    lineHeight: 0,
                    "&:focus-visible": {
                      outline: `2px solid ${BRAND.teal}`,
                      outlineOffset: 4,
                    },
                  }}
                >
                  <Box sx={{ width: logoSize, height: logoSize, pointerEvents: "none", flexShrink: 0 }}>
                    <AnimatedBrandLogo
                      idSuffix="navbar"
                      entrance="immediate"
                      alt="PatientMesh"
                      size={logoSize}
                      sx={{ width: "100%", height: "100%", ml: 0, mr: 0 }}
                    />
                  </Box>
                  <Box
                    component="span"
                    sx={{

                      fontWeight: 700,
                      fontSize: { xs: "1.15rem", md: isCompactNav ? "1.35rem" : "1.45rem" },
                      letterSpacing: "-0.02em",
                      display: "flex",
                      alignItems: "center",
                      userSelect: "none",
                      lineHeight: 1,
                    }}
                  >
                    <Box component="span" sx={{ color: BRAND.navy }}>
                      Patient
                    </Box>
                    <Box component="span" sx={{ color: BRAND.teal }}>
                      Mesh
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Orta: menü linkleri */}
              {!isMobile && (
                <Box
                  sx={{
                    position: "absolute",
                    left: "50%",
                    top: 0,
                    height: "100%",
                    transform: "translateX(-50%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pt: { xs: 2, md: 2.75 },
                    gap: { md: 0.25, lg: 0.5 },
                    zIndex: 1,
                    pointerEvents: "none",
                    "& > *": { pointerEvents: "auto" },
                  }}
                >
                  {leftItems.map((item) => (
                    <NavLink key={item.text} onClick={item.onClick}>
                      {item.text}
                    </NavLink>
                  ))}
                  <NavLink onClick={() => navigate("/meeting")}>{t("pricing")}</NavLink>
                </Box>
              )}

              {/* Sağ: hesap + dil */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: { xs: 0.5, md: 0.75 },
                  flexShrink: 0,
                  zIndex: 2,
                }}
              >
                {!isMobile && (
                  isLoggedIn ? (
                    <Button
                      variant="contained"
                      onClick={() => navigate(dashboardPath)}
                      startIcon={<DashboardIcon sx={{ fontSize: 20 }} />}
                      sx={{
                        height: 32,
                        px: 1.5,
                        background: `linear-gradient(135deg, ${BRAND.tealDark} 0%, ${BRAND.teal} 100%)`,
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: { xs: "0.80rem", md: "0.85rem" },
                        textTransform: "none",
                        borderRadius: "8px",
                        border: `1px solid ${BRAND.teal}66`,
                        boxShadow: "0 6px 20px rgba(15, 123, 107, 0.35)",
                        transition: "all 0.25s ease",
                        "&:hover": {
                          background: `linear-gradient(135deg, ${BRAND.teal} 0%, #25c9ad 100%)`,
                          boxShadow: `0 8px 28px rgba(29, 184, 160, 0.45)`,
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      {t("goToPanel", { defaultValue: "Panele Git" })}
                    </Button>
                  ) : (
                    <>
                      <AccountMenuButton
                        items={loginMenuItems}
                        onNavigate={(path) => navigate(path)}
                        hoverOpen
                        label={t("login", { defaultValue: "Giriş Yap" })}
                      />
                      <AccountMenuButton
                        items={registerMenuItems}
                        onNavigate={(path) => navigate(path)}
                        hoverOpen
                        label={t("register", { defaultValue: "Kayıt Ol" })}
                      />
                    </>
                  )
                )}
                {isMobile && (
                  isLoggedIn ? (
                    <Button
                      onClick={() => navigate(dashboardPath)}
                      sx={{
                        minWidth: 0,
                        p: { xs: "6px", sm: "6px 12px" },
                        color: BRAND.navy,
                        border: `1px solid ${BRAND.borderLight}`,
                        borderRadius: "12px",
                        background: "rgba(255,255,255,0.8)",
                        "&:hover": { background: BRAND.tealLight, borderColor: `${BRAND.teal}66` },
                      }}
                    >
                      <DashboardIcon sx={{ fontSize: 20, mr: { sm: 0.5 } }} />
                      <Box component="span" sx={{ display: { xs: "none", sm: "inline" }, fontWeight: 700, fontSize: "0.85rem", textTransform: "none" }}>
                        {t("goToPanel", { defaultValue: "Panele Git" })}
                      </Box>
                    </Button>
                  ) : (
                    <Button
                      onClick={handleMobileAuthOpen}
                      sx={{
                        minWidth: 0,
                        p: { xs: "6px", sm: "6px 12px" },
                        color: BRAND.navy,
                        border: `1px solid ${BRAND.borderLight}`,
                        borderRadius: "12px",
                        background: "rgba(255,255,255,0.8)",
                        "&:hover": { background: BRAND.tealLight, borderColor: `${BRAND.teal}66` },
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 20, mr: { sm: 0.5 } }} />
                      <Box component="span" sx={{ display: { xs: "none", sm: "inline" }, fontWeight: 700, fontSize: "0.85rem", textTransform: "none" }}>
                        {t("accountMenu", { defaultValue: "Giriş / Kayıt" })}
                      </Box>
                    </Button>
                  )
                )}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    pl: { xs: 0.5, md: 1 },
                    ml: { md: 0.5 },
                    borderLeft: { md: `1px solid ${BRAND.borderLight}` },
                  }}
                >
                  <LanguageSelector variant="marketing" size="small" />
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>
      </AppBar>

      <Popover
        open={Boolean(mobileAuthAnchor)}
        anchorEl={mobileAuthAnchor}
        onClose={handleMobileAuthClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableScrollLock
        hideBackdrop
        sx={{ pointerEvents: "none", zIndex: 1400 }}
        PaperProps={{ sx: { pointerEvents: "auto", ...authPanelSx } }}
      >
        <ClickAwayListener onClickAway={handleMobileAuthClose}>
          <Box sx={{ width: "100%", height: "100%" }}>
            <Typography
              sx={{
                px: 1.25,
                pt: 0.5,
                pb: 1,
                fontSize: "0.68rem",
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: BRAND.textSoft,
              }}
            >
              {t("login", { defaultValue: "Giriş Yap" })}
            </Typography>
            {loginMenuItems.map((item) => (
              <AuthMenuItem
                key={item.key}
                icon={item.icon}
                label={item.label}
                highlight={item.highlight}
                  onClick={() => {
                  navigate(item.path);
                  handleMobileAuthClose();
                }}
              />
            ))}
            <Typography
              sx={{
                px: 1.25,
                pt: 2,
                pb: 1,
                fontSize: "0.68rem",
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: BRAND.textSoft,
              }}
            >
              {t("register", { defaultValue: "Kayıt Ol" })}
            </Typography>
            {registerMenuItems.map((item) => (
              <AuthMenuItem
                key={item.key}
                icon={item.icon}
                label={item.label}
                highlight={item.highlight}
                  onClick={() => {
                  navigate(item.path);
                  handleMobileAuthClose();
                }}
              />
            ))}
          </Box>
        </ClickAwayListener>
      </Popover>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerClose}
        hideBackdrop
        sx={{ zIndex: 1500, pointerEvents: "none" }}
        PaperProps={{
          sx: {
            pointerEvents: "auto",
            bgcolor: BRAND.pageBg,
            color: BRAND.navy,
            borderRight: `1px solid ${BRAND.borderLight}`,
          },
        }}
      >
        <ClickAwayListener onClickAway={handleDrawerClose}>
          <Box sx={{ width: 300, pt: 2, height: "100%", display: "flex", flexDirection: "column" }}>
            <Box
              component="button"
              type="button"
              onClick={() => {
                goHome();
                handleDrawerClose();
              }}
              sx={{
                display: "flex",
                justifyContent: "center",
                pb: 2,
                width: "100%",
                border: "none",
                background: "transparent",
                cursor: "pointer",
              }}
            >
              <AnimatedBrandLogo
              idSuffix="navbar-drawer"
              entrance="immediate"
              alt="PatientMesh"
              size={112}
              sx={{ ml: 0, mr: 0 }}
            />
          </Box>
          <Divider sx={{ borderColor: BRAND.borderLight }} />
          <List sx={{ px: 1, py: 1 }}>
            {drawerItems.map((item) => (
              <ListItem
                button
                key={item.text}
                onClick={() => {
                  item.onClick();
                    handleDrawerClose();
                }}
                sx={{
                  borderRadius: "12px",
                  mb: 0.5,
                  "&:hover": { bgcolor: "rgba(29, 184, 160, 0.12)" },
                }}
              >
                <ListItemIcon sx={{ color: BRAND.teal, minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: 600 }} />
              </ListItem>
            ))}
          </List>
          <Divider sx={{ borderColor: BRAND.borderLight, my: 1 }} />
          <Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
            {isLoggedIn ? (
              <Button
                variant="contained"
                onClick={() => {
                  navigate(dashboardPath);
                  handleDrawerClose();
                }}
                startIcon={<DashboardIcon sx={{ fontSize: 20 }} />}
                sx={{
                  height: 40,
                  mt: 1,
                  background: `linear-gradient(135deg, ${BRAND.tealDark} 0%, ${BRAND.teal} 100%)`,
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  textTransform: "none",
                  borderRadius: "12px",
                  boxShadow: "0 4px 14px rgba(15, 123, 107, 0.25)",
                }}
              >
                {t("goToPanel", { defaultValue: "Panele Git" })}
              </Button>
            ) : (
              <>
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: BRAND.textSoft,
                    mb: 0.5,
                    mt: 1,
                    px: 0.5,
                  }}
                >
                  {t("login", { defaultValue: "Giriş Yap" })}
                </Typography>
                {loginMenuItems.map((item) => (
                  <AuthMenuItem
                    key={item.key}
                    icon={item.icon}
                    label={item.label}
                    highlight={item.highlight}
                    onClick={() => {
                      navigate(item.path);
                      handleDrawerClose();
                    }}
                  />
                ))}
                
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: BRAND.textSoft,
                    mb: 0.5,
                    mt: 2,
                    px: 0.5,
                  }}
                >
                  {t("register", { defaultValue: "Kayıt Ol" })}
                </Typography>
                {registerMenuItems.map((item) => (
                  <AuthMenuItem
                    key={item.key}
                    icon={item.icon}
                    label={item.label}
                    highlight={item.highlight}
                    onClick={() => {
                      navigate(item.path);
                      handleDrawerClose();
                    }}
                  />
                ))}
              </>
            )}
          </Box>
        </Box>
        </ClickAwayListener>
      </Drawer>
    </>
  );
}
